import {
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonLabel,
  IonMenuToggle,
  IonPage,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import React from "react";
import { menu } from "ionicons/icons";
import { CategorieLinks } from "../../components/CategorieLinks/CategorieLinks";
import PictureCard from "../Pictures/PictureCard";
import MasonryGrid from "../../components/MasonryGrid/MasonryGrid";
import { useCommon } from "../../common/useCommon";
import { usePictures } from "../Pictures/usePictures";
import ToolbarIcons from "../../components/ToolbarIcons/ToolbarIcons";
import {
  ADDITIONAL_EQUIPMENT_CATEGORY,
  Currency,
} from "../../common/constants";
import { FrameAssembledBihWarning } from "../../components/FrameAssembledBihWarning/FrameAssembledBihWarning";
import PicturePreview from "../Pictures/PicturePreview";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "@ionic/react/css/ionic-swiper.css";
import { useEffect, useMemo, useState } from "react";
import { IPicture } from "../../models";
import { useCart } from "../Cart/useCart";
import { useCartNotification } from "../Cart/useCartNotification";
import Seo from "../../common/Seo";

interface PictureProps {
  big: string;
  small: string;
}

const Picture: React.FC<PictureProps> = ({ small, big }) => (
  <picture>
    <source media="(min-width:650px)" srcSet={`assets/img/start/${big}`} />
    <img
      src={`assets/img/start/${small}`}
      alt="Bojenje po brojevima"
      style={{ width: "100%" }}
    />
  </picture>
);

const Start = () => {
  const {
    cart,
    frames,
    framesAssembled,
    changeFrameNumber,
    changeFrameAssembledNumber,
  } = useCart();
  const { cartNotification } = useCartNotification();
  const { zoom } = useCommon();
  const { allPictures, availableOnly, favorites, getAllPictures } =
    usePictures();

  const [pictures, setPictures] = useState<IPicture[]>([]);
  const { currency, setCurrency, conversionRate } = useCommon();
  const [showAssembledWarning, setShowAssembledWarning] =
    useState<boolean>(false);

  const onCurrencyChange = (value: Currency) => {
    if (value === "KM" && framesAssembled > 0) {
      setShowAssembledWarning(true);
    } else {
      setCurrency(value);
    }
  };

  useEffect(() => {
    if (allPictures.length === 0) {
      getAllPictures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const computedPictures = useMemo(() => {
    const filtered = allPictures
      .filter(
        (p) =>
          p.category !== ADDITIONAL_EQUIPMENT_CATEGORY &&
          (!availableOnly || p.status),
      )
      .sort(() => Math.random() - 0.5);

    // Ensure reservations (status false/0) are placed at the end
    return filtered.sort((a, b) => Number(b.status) - Number(a.status));
    // we want to run randomization of each of dependancies change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPictures, availableOnly, cart, favorites]);

  useEffect(() => {
    setPictures(computedPictures);
  }, [computedPictures]);

  return (
    <IonPage id="start-page">
      <Seo
        isHome
        title="Bojenje po brojevima"
        description="Bojenje po brojevima, Crtanje po brojevima. Zabavite se, smanjite stres, probudite umetničku dušu. Idealna zanimacija za sve uzraste, savršen poklon za svaku priliku."
        path="/"
      />
      <IonHeader no-border>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonMenuToggle>
              <IonIcon icon={menu} style={{ fontSize: 28 }} />
            </IonMenuToggle>
          </IonButtons>
          <IonTitle>Bojenje po brojevima</IonTitle>
          <ToolbarIcons />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <h1 className="sr-only">
          Bojenje po brojevima — setovi za crtanje i bojenje po brojevima
        </h1>
        <IonGrid className="ion-no-padding">
          <IonRow>
            <IonCol size="12" sizeMd="6" offsetMd="3">
              <img src="assets/BPB_logo.png" alt="Bojenje po brojevima logo" />
            </IonCol>
          </IonRow>
        </IonGrid>

        {/* Currency picker */}
        <IonSegment
          style={{ maxWidth: "400px", margin: "2em auto" }}
          value={currency}
          onIonChange={(e) => onCurrencyChange(e.detail.value as Currency)}
        >
          <IonSegmentButton value="DIN">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "1em",
              }}
            >
              <img
                src="assets/flags/SRB.svg"
                alt="Srbija"
                style={{ display: "flex", width: "3em" }}
              />
              <IonLabel>SRB</IonLabel>
            </div>
          </IonSegmentButton>
          <IonSegmentButton value="KM" disabled={conversionRate <= 0}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "1em",
              }}
            >
              <img
                src="assets/flags/BIH.svg"
                alt="Bosna i Hercegovina"
                style={{ display: "flex", width: "3em" }}
              />
              <IonLabel>BIH</IonLabel>
            </div>
          </IonSegmentButton>
        </IonSegment>

        <IonGrid className="ion-no-padding">
          <IonRow>
            <IonCol size="12" sizeMd="8" offsetMd="2">
              <IonCard>
                <IonCardHeader className="b f15 ion-text-center">
                  Dobrodošli u šareni svet
                </IonCardHeader>
                <IonCardContent>
                  <Swiper
                    modules={[Autoplay, Navigation]}
                    autoplay={true}
                    navigation
                    // onSwiper={(swiper: unknown) => console.log(swiper)}
                  >
                    <SwiperSlide>
                      <Picture
                        small="bpb_start_01_small.jpg"
                        big="bpb_start_01.jpg"
                      />
                    </SwiperSlide>
                    <SwiperSlide>
                      <Picture
                        small="bpb_start_03_small.jpg"
                        big="bpb_start_03.jpg"
                      />
                    </SwiperSlide>
                    <SwiperSlide>
                      <Picture
                        small="bpb_start_02_small.jpg"
                        big="bpb_start_02.jpg"
                      />
                    </SwiperSlide>
                    <SwiperSlide>
                      <Picture
                        small="bpb_start_04_small.jpg"
                        big="bpb_start_04.jpg"
                      />
                    </SwiperSlide>
                    <SwiperSlide>
                      <Picture
                        small="bpb_start_05_small.jpg"
                        big="bpb_start_05.jpg"
                      />
                    </SwiperSlide>
                    <SwiperSlide>
                      <Picture
                        small="bpb_start_07_small.jpg"
                        big="bpb_start_07.jpg"
                      />
                    </SwiperSlide>
                    <SwiperSlide>
                      <Picture
                        small="bpb_start_06_small.jpg"
                        big="bpb_start_06.jpg"
                      />
                    </SwiperSlide>
                  </Swiper>

                  <CategorieLinks />
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>
        </IonGrid>
        <PicturePreview />

        <MasonryGrid columns={zoom} mobileColumns={Math.min(zoom, 2)}>
          {pictures.map((pic) => (
            <PictureCard key={pic.id} pic={pic} />
          ))}
        </MasonryGrid>
        <FrameAssembledBihWarning
          isOpen={showAssembledWarning}
          framesAssembled={framesAssembled}
          onDismiss={() => setShowAssembledWarning(false)}
          onRemove={() => {
            changeFrameAssembledNumber(0);
            setCurrency("KM");
            setShowAssembledWarning(false);
          }}
          onConvert={() => {
            changeFrameNumber(frames + framesAssembled);
            changeFrameAssembledNumber(0);
            setCurrency("KM");
            setShowAssembledWarning(false);
          }}
        />
        {cartNotification}
      </IonContent>
    </IonPage>
  );
};

export default Start;
